// ── Standalone task detail page (C1167) ──
// Deep-linkable counterpart to the slide-out panel in components/task-detail.js.
// Reuses that module's renderer + every edit handler via mountTaskDetail() —
// see that file's "Two Surfaces, One Renderer" notes for how ownership works.

import { checkAuth, getUser } from '../auth.js';
import { api } from '../api.js';
import { closeModal } from '../ui.js';
import { escapeHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { mountTaskDetail } from '../components/task-detail.js';
import { removeAssigneePicker } from '../components/assignee-picker.js';

let state = { projectId: null, taskKey: null, unmount: null };

function resetState() {
  state = { projectId: null, taskKey: null, unmount: null };
}

export async function taskDetailPage(params) {
  if (!(await checkAuth())) return;

  resetState();
  const projectId = params.id;
  const taskKey = decodeURIComponent(params.taskKey);
  state.projectId = projectId;
  state.taskKey = taskKey;

  initLayout(getUser());
  updateLayoutContext({ projectId, projectName: null, activeView: 'board', viewLabel: taskKey });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header task-detail-page-header">
      <a class="btn btn-ghost btn-sm" href="#/projects/${projectId}/board">&larr; Board</a>
      <h2>${escapeHtml(taskKey)}</h2>
    </div>
    <div class="task-detail-page" id="task-detail-host">
      <div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>
    </div>`;
  // .task-detail-page here styles the first (pre-mount) paint above; mountTaskDetail() also
  // asserts the same class on this host so the page-mode CSS hook doesn't depend on this
  // markup alone (see components/task-detail.js).

  const host = content.querySelector('#task-detail-host');

  // Prefetch full project task list so children/parent/tag-suggestions/sprint
  // options resolve exactly like the panel does. Sequential (not parallel with
  // the detail fetch) — avoids a two-phase flash where those sections pop in
  // late. A failure here degrades softly: the task itself still loads.
  let allTasks = [];
  try {
    const res = await api.get('/projects/' + projectId + '/tasks');
    allTasks = res.tasks || [];
  } catch (_) {
    // non-fatal — allTasks-dependent sections (children/parent/tags) just render empty
  }

  try {
    state.unmount = await mountTaskDetail(host, projectId, taskKey, { allTasks });
  } catch (err) {
    host.innerHTML = `<div class="empty text-muted">
      ${escapeHtml(err.message || 'Failed to load task')}
      <div class="mt-1"><a href="#/projects/${projectId}/board">Back to board</a></div>
    </div>`;
  }

  return () => {
    closeModal();
    removeAssigneePicker();
    if (state.unmount) state.unmount();
    resetState();
  };
}
